package com.swetdot.swetgram

import com.swetdot.swetgram.data.api.ApiClient
import com.swetdot.swetgram.data.api.ApiConfig
import com.swetdot.swetgram.data.api.ClientInfo
import com.swetdot.swetgram.data.api.createHttpClient
import com.swetdot.swetgram.data.api.createMediaHttpClient
import com.swetdot.swetgram.data.api.createRealtimeHttpClient
import com.swetdot.swetgram.data.push.PushRegistrar
import com.swetdot.swetgram.data.push.PushRegistrationRepository
import com.swetdot.swetgram.data.realtime.RealtimeClient
import com.swetdot.swetgram.data.realtime.RealtimeRepository
import com.swetdot.swetgram.data.auth.AuthRepository
import com.swetdot.swetgram.data.auth.SessionManager
import com.swetdot.swetgram.data.auth.SessionStorage
import com.swetdot.swetgram.data.ChatRepository
import com.swetdot.swetgram.data.MessageRepository
import com.swetdot.swetgram.data.discover.DiscoverRepository
import com.swetdot.swetgram.data.groups.GroupRepository
import com.swetdot.swetgram.data.picso.PicsoRepository
import com.swetdot.swetgram.data.links.LinkRepository
import com.swetdot.swetgram.data.legal.LegalRepository
import com.swetdot.swetgram.data.media.ImageSource
import com.swetdot.swetgram.data.media.UploadRepository
import com.swetdot.swetgram.data.media.VoicePlayer
import com.swetdot.swetgram.data.media.VoiceRecorder
import com.swetdot.swetgram.data.moderation.AdminRepository
import com.swetdot.swetgram.data.moderation.ReportRepository
import com.swetdot.swetgram.data.notifications.NotificationSettingsRepository
import com.swetdot.swetgram.data.profile.BlockRepository
import com.swetdot.swetgram.data.profile.PrivacyRepository
import com.swetdot.swetgram.data.profile.ProfileRepository
import com.swetdot.swetgram.data.premium.PremiumRepository
import com.swetdot.swetgram.data.premium.PremiumBilling
import com.swetdot.swetgram.data.tasks.TasksRepository
import com.swetdot.swetgram.data.tasks.DailyStepsSource
import com.swetdot.swetgram.data.shop.XpShopRepository
import com.swetdot.swetgram.data.streaks.SquadStreakRepository
import com.swetdot.swetgram.data.cache.DatabaseDriverFactory
import com.swetdot.swetgram.data.cache.clearAll
import com.swetdot.swetgram.data.cache.createCache
import com.swetdot.swetgram.protocol.dto.AuthSessionResponse
import io.ktor.client.HttpClient

/**
 * Composition root. Hand-wired rather than delegated to a DI framework: the graph is small,
 * and one readable file beats a container whose wiring only reveals itself at runtime.
 *
 * Platform pieces (the HTTP engine, credential storage) arrive through the constructor, so this
 * whole object is constructible in a test with fakes.
 */
class AppContainer(
    sessionStorage: SessionStorage,
    /**
     * The RAW engine client, retained as a property because one collaborator genuinely needs it
     * unconfigured: [uploads] PUTs straight to object storage, which must not receive this app's
     * `Authorization` header. See [UploadRepository].
     */
    private val engine: HttpClient,
    val clientInfo: ClientInfo,
    driverFactory: DatabaseDriverFactory,
    baseUrl: String = ApiConfig.BASE_URL,
    realtimeUrl: String = ApiConfig.REALTIME_URL,
) {
    val session = SessionManager(sessionStorage)

    /**
     * Set once [auth] exists, breaking the cycle between "the API client needs a token" and
     * "refreshing a token needs the API client". A plain nullable var is honest about the
     * one-frame gap; a lateinit would only hide it.
     */
    private var exchangeRefreshToken: (suspend (String) -> AuthSessionResponse)? = null

    /**
     * Called before every request. Refreshing HERE rather than reacting to a 401 means the
     * common case never produces a failed request at all -- and because
     * [SessionManager.refreshIfNeeded] serialises, a burst of parallel requests on app resume
     * still performs exactly one token exchange.
     */
    private suspend fun currentToken(): String? {
        val exchange = exchangeRefreshToken ?: return session.accessTokenOrNull()
        return runCatching { session.refreshIfNeeded(exchange) }
            // A failed refresh must not block the request from being attempted; the server's
            // 401 is then the authority, and ApiClient turns a revoked session into a sign-out.
            .getOrElse { session.accessTokenOrNull() }
    }

    private val http: HttpClient = createHttpClient(
        engineClient = engine,
        clientInfo = clientInfo,
        baseUrl = baseUrl,
        tokenProvider = { currentToken() },
    )

    val api = ApiClient(
        http = http,
        tokenProvider = { currentToken() },
        // The contract revokes the whole session on refresh-token reuse, so there is nothing
        // to recover: drop the local session and let the UI fall back to sign-in.
        //
        // The cache goes too. This is the sign-out nobody tapped -- a revoked or reused refresh
        // token -- and it leaves the device signed out just as thoroughly as the button does, so
        // it must leave just as little of the previous account behind. Referencing [cache], which
        // is constructed below, is safe because the lambda reads it when the session expires
        // rather than when this object is built.
        onSessionExpired = {
            session.signOut()
            runCatching { cache.clearAll() }
            // The signed-in profile goes with the conversations, and for the same reason: it is
            // held in memory by an object that outlives the navigation graph, so leaving it would
            // show the previous account's name and photo on the Profile tab of whoever signs in
            // next. See [ProfileRepository.forget].
            profiles.forget()
        },
    )

    val auth: AuthRepository = AuthRepository(
        api = api,
        session = session,
        onSignedOut = {
            cache.clearAll()
            profiles.forget()
        },
    ).also {
        exchangeRefreshToken = it::exchangeRefreshToken
    }

    /** The UI reads from here; the network only ever writes into it. */
    val cache = createCache(driverFactory)

    val chats = ChatRepository(api, cache)

    val messages = MessageRepository(api, cache)

    val pushTokens = PushRegistrationRepository(api, clientInfo)

    val notificationSettings = NotificationSettingsRepository(api)

    /**
     * Profile, privacy and blocking. Unlike [chats] and [messages] these take no cache: every
     * screen behind them is a settings form the user opened deliberately, where showing a stale
     * answer to "who can see when I am online" is worse than showing a spinner.
     */
    // The type is spelled out because [api] and [auth] both call `profiles.forget()` from their
    // sign-out lambdas, which makes this declaration's type depend on itself as far as inference
    // is concerned. The lambdas run long after construction, so the cycle is only in the compiler.
    val profiles: ProfileRepository = ProfileRepository(api)

    val privacy = PrivacyRepository(api)

    val blocks = BlockRepository(api)

    val legal = LegalRepository(api)

    /** Authoritative Swetgram Black entitlement; intentionally not inferred from local billing. */
    val premium = PremiumRepository(api)

    /** Activity-bound store UI. Backend [premium] remains the sole entitlement authority. */
    var premiumBilling: PremiumBilling? = null

    /**
     * Groups: the tab's list, creation, one group, and its members.
     *
     * No cache, like [profiles] and unlike [chats]. There is no SQLDelight `groups` table, so the
     * Groups tab opens on skeletons rather than on a stale list -- recorded as owed in
     * `GroupRepository`, together with the `listGroups` ETag that cannot be used until there is
     * somewhere to serve a 304 from.
     */
    val groups = GroupRepository(api)

    /** Resolves immutable profile/group QR targets and username-based public links. */
    val links = LinkRepository(api)

    /**
     * Discover: the global feed, one post with its thread, and the writes on both.
     *
     * Its first page is cached in SQLDelight and rendered immediately on the next launch, then
     * revalidated in the background. Cache reset owns the account-bound cleanup on sign-out.
     *
     * TWO THINGS THIS DELIBERATELY DOES NOT DO, both recorded in `DECISIONS.md` §20:
     *
     *  * NO POLLING AND NO REALTIME SUBSCRIPTION. Discover has no push, no outbox event and no
     *    realtime frame, so nothing here can be told that a post or a comment arrived. The feed
     *    says when it was last read instead of pretending to be live -- see `DiscoverViewModel`.
     *  * Conditional reads carry the validator stored with that cached head, so a fresh launch can
     *    keep the rendered page when the server answers 304.
     */
    val discover = DiscoverRepository(api, cache)
    val picso = PicsoRepository(api)

    /** Cached daily missions; social completion is still verified only by the backend. */
    val tasks = TasksRepository(api, cache)

    /** XP shop catalog and item purchasing. */
    val xpShop = XpShopRepository(api)

    /** Duo and group streaks in conversations. */
    val squadStreaks = SquadStreakRepository(api)

    /** Activity-bound voluntary step-permission bridge. Null on unsupported platforms. */
    var dailyStepsSource: DailyStepsSource? = null

    /**
     * SPS §16.3's report: `createReport`, and nothing else.
     *
     * NON-NULL, which is what turns every Report action in the app on. Each of the five surfaces
     * SPS §16.3 names draws its Report control only when it is handed a submitter, and hides it
     * entirely otherwise -- a Report that silently does nothing is worse than none at all on a
     * safety control, because the user believes they have been heard. So this property existing IS
     * the feature flag, and there is deliberately no other one.
     *
     * No cache, like [profiles]: a report is a write with a receipt, and there is nothing to read
     * back. v1.0.0 declares no operation that lets a reporter see their own report again.
     */
    val reports = ReportRepository(api)

    /**
     * SPS §16.4's moderation: the eight operations the contract tags `Admin`.
     *
     * CONSTRUCTED FOR EVERY ACCOUNT, including ordinary ones, and that is correct rather than
     * careless. It is a handle onto eight endpoints that answer `403 admin_role_required` unless
     * `users.role` is `moderator` or `admin`; building it conditionally would only mean the client
     * decided who is staff, and the client does not decide that. `canOpenModeration(role)` gates
     * what is DRAWN -- and it reads the role `getMe` actually returned, so an ordinary account sees
     * no moderation entry anywhere.
     *
     * No cache, and `AdminRepository` refuses one on its own terms: a queue rendered from a stale
     * local copy shows reports a colleague has already decided, and acting on one of those is how
     * the same account gets suspended twice for one thing.
     */
    val admin = AdminRepository(api)

    /**
     * Image upload: presign, PUT to storage, complete.
     *
     * Deliberately handed [engine] rather than [http]. The middle step is addressed to a third
     * party, and the configured client would attach this session's bearer token and rewrite the
     * URL against `/api/v1` -- see [UploadRepository] for why that is a leak rather than an
     * inconvenience.
     */
    val uploads = UploadRepository(api, engine)

    /**
     * The client Coil fetches avatars and message photos with.
     *
     * [engine] again, and for the same reason [uploads] gets it: those URLs point at the media
     * origin, not at the API, so the configured client would attach this session's bearer token to
     * every image GET and then rewrite the URL against `/api/v1`. See `createMediaHttpClient`.
     */
    val imageHttp: HttpClient = createMediaHttpClient(engine)

    /**
     * Set by the platform entry point where a chooser can be presented -- an `Activity` on
     * Android, a `UIViewController` on iOS. Neither exists in `:shared`, which is why this is a
     * hole the app target fills rather than something constructed here.
     *
     * Null means the app cannot pick images, and every surface that offers to must hide its
     * affordance rather than showing a button that does nothing.
     */
    var imagePicker: ImageSource? = null

    /** Activity-bound microphone permission bridge plus process-wide audio playback. */
    var voiceRecorder: VoiceRecorder? = null
    var voicePlayer: VoicePlayer? = null

    /**
     * Set by the platform entry point where a push service exists -- Android today, iOS when
     * APNs is wired up. Null means the app simply does not register for push, which is the
     * correct behaviour on a platform that cannot receive it.
     */
    var pushRegistrar: PushRegistrar? = null

    /**
     * Realtime notifications. Started only once a session exists -- issuing a connection token
     * is an authenticated call, so connecting before sign-in would just fail in a loop.
     */
    val realtime = RealtimeClient(
        http = createRealtimeHttpClient(engine),
        tokenSource = RealtimeRepository(api),
        url = realtimeUrl,
    )
}
